// src/pages/api/bcAPI.ts — Business Control review API (with EKD lineage).
import { apiGet, apiPost } from "../API";
const BASE = "/adriva_api/bc";

export interface RefDomainLink { domain_id: string; name?: string; value_count?: number; lookup_entity_id?: string; required?: boolean; governance_status?: string; }
export interface Lineage {
  fields: { id: string; name: string }[];
  reference_domains: RefDomainLink[];
  relationship_ids: string[]; grain_ids: string[];
  knowledge_gap_ids: { id: string; text: string }[]; reference_candidate_ids: string[];
}
export interface BcControlView {
  control_id: string; title: string; category?: string; family?: string; action_type?: string;
  lane_name?: string; readiness?: string; disposition?: string; confidence?: number; authority?: string;
  requires_reference_data?: boolean; rule_generation_eligible?: boolean; execution_ready?: boolean;
  owner_field?: string; question?: string; lineage: Lineage; status: string; note?: string;
  business_statement?: string; business_intent?: string; condition?: string; expected_outcome?: string;
  business_reason?: string; business_impact?: string[]; evidence_type?: string; business_expression?: string;
  evidence?: { statement?: string; excerpt?: string; type?: string; authority?: string; confidence?: number }[];
  participating_fields?: { name: string; field_id?: string; role?: string; confidence?: number }[];
  inferred_details?: { field?: string; statement?: string; confidence?: number }[];
  readiness_assessment?: { draft_rule_eligible?: boolean; blocking_reasons?: string[]; warnings?: string[]; sme_approval_required?: boolean };
  challenge_issues?: { severity?: string; message?: string; recommendation?: string }[];
  control_logic?: { left?: string; operator?: string; right?: string; value?: any } | null;
  grain_fields?: string[]; target_tables?: string[]; tags?: string[];
  quality?: { score?: number; dimensions?: Record<string, number>; issues?: string[] };
  derivation?: any; reference_ready?: boolean; reference_status?: string; reference_domain_names?: string[]; lookup_targets?: string[];
  status_bucket?: string; review_state?: "rule_ready" | "review_suggested" | "assessment_pending"; deviation?: string;
}
export interface BcLaneMeta { label: string; total: number; count: number; confirmed: number; resolved: number; needs_work: number; pending_review: number; pending: number; }
export interface BcSummary {
  success: boolean; entity_id: string;
  totals: { controls: number; sme_decisions: number; confirmed: number };
  lanes: Record<string, BcLaneMeta>;
  by_category: Record<string, number>; by_readiness: Record<string, number>; confidence?: number;
  overall?: { controls: number; resolved_good: number; needs_work: number };
  reference_data?: { total: number; resolved_governed: number; awaiting_canonical_values: number; not_governed_in_lookup: number; lookup_governed_fields: number; domains_total: number; domains_with_values: number; domains_awaiting_values: number };
  ek_lineage: { reference_domains?: number; linked_reference_domains?: number; fields?: number; relationships?: number; generated_at?: string };
}
export interface BcLaneResponse { success: boolean; entity_id: string; lane: string; label: string; action_lane: boolean; controls: BcControlView[]; counts: { total: number; confirmed: number; flagged: number; pending: number }; }

export function bc_getSummary(entityId: string): Promise<BcSummary> { return apiGet(`${BASE}/${encodeURIComponent(entityId)}/summary`); }
export function bc_getLane(entityId: string, lane: string): Promise<BcLaneResponse> { return apiGet(`${BASE}/${encodeURIComponent(entityId)}/lane/${lane}`); }
export function bc_getControl(entityId: string, controlId: string): Promise<{ success: boolean; control: BcControlView }> { return apiGet(`${BASE}/${encodeURIComponent(entityId)}/control/${encodeURIComponent(controlId)}`); }
export function bc_review(entityId: string, control_id: string, status: string, note = ""): Promise<BcLaneResponse> { return apiPost(`${BASE}/${encodeURIComponent(entityId)}/review`, { control_id, status, note }); }

// ── Status-first review (SME decision state) ────────────────────────────────
// A second lens over the same controls: navigate by what the SME must do
// (needs you / awaiting values / ready / excluded) instead of by derivation lane.

export type BcStatusKey = "needs_you" | "awaiting_values" | "ready" | "excluded";

export interface BcStatusSummary {
  success: boolean;
  entity_id: string;
  total: number;
  confidence?: number;
  statuses: {
    needs_you: { count: number; pending: number; confirmed: number; flagged: number };
    awaiting_values: { count: number };
    ready: { count: number; rule_ready: number; review_suggested: number; assessment_pending: number };
    excluded: { count: number };
  };
  order: BcStatusKey[];
  labels: Record<BcStatusKey, string>;
  review_suggested: number;
  deviations: { count: number; flagged: number; ungoverned_reference: number };
  ek_lineage: { reference_domains?: number; linked_reference_domains?: number; fields?: number; relationships?: number; generated_at?: string };
}

export interface BcFacet { kind: "ready_sub" | "lane"; items: { key: string; label: string; count: number }[]; }

export interface BcStatusResponse {
  success: boolean;
  entity_id: string;
  status: BcStatusKey;
  label: string;
  action_status: boolean;
  facets: BcFacet;
  controls: BcControlView[];
  counts: { total: number; confirmed: number; flagged: number; pending: number };
}

export function bc_getStatusSummary(entityId: string): Promise<BcStatusSummary> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/status-summary`);
}
export function bc_getStatus(entityId: string, status: BcStatusKey, sub?: string, lane?: string): Promise<BcStatusResponse> {
  const qs = new URLSearchParams();
  if (sub) qs.set("sub", sub);
  if (lane) qs.set("lane", lane);
  const q = qs.toString();
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/status/${status}${q ? "?" + q : ""}`);
}

// ── Rule preview: compile a control (app.dq) and run it on sample data ───────
export interface BcRulePreview {
  success: boolean;
  entity_id: string;
  control_id: string;
  engine_available?: boolean;
  provisional?: boolean;         // compiled with the eligibility gate lifted (Needs-you preview)
  compilation?: { status: string; reason?: string; family?: string; enforcement?: string | null };
  rule?: {
    rule_id?: string; control_id?: string; family?: string; execution_scope?: string;
    target_table?: string; predicate?: any; applicability?: any;
    enforcement?: "enforcing" | "candidate"; dimension?: string; description?: string; meta?: any;
  } | null;
  preview?: string | null;          // human-readable predicate
  executable?: boolean;
  execution_note?: string;
  execution?: {
    status: "PASS" | "FAIL" | "NO_DATA" | "SKIPPED" | "ERROR";
    engine?: string; dimension?: string; enforcement?: string;
    total_records?: number; applicable_records?: number;
    records_passed?: number; records_failed?: number;
    violation_rate?: number; pass_rate?: number;
    sample_violations?: Record<string, any>[];
    missing_tables?: string[]; error?: string; sample_source?: string;
  } | null;
}

export function bc_getControlPreview(entityId: string, controlId: string): Promise<BcRulePreview> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/control/${encodeURIComponent(controlId)}/preview`);
}
